import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { OnlyAcceptNumbers } from 'src/assets/functions/only-accept-numbers';
import { ConstantsFordProtect } from '../../../models/constants/constants-ford-protect';
import { PriceEdit } from '../../../models/product/price-edit';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';
import { PriceService } from '../../../services/price/price.service';

type DropdownChangeEvent<T> = {
  value: T;
};

@Component({
  selector: 'app-price-details-edit',
  templateUrl: './price-details-edit.component.html',
  styleUrls: ['./price-details-edit.component.scss'],
  standalone: false
})
export class PriceDetailsEditComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  listNamePrice = [
    { label: 'TRANSLATE.SELECT' },
    { label: 'TRANSLATE.SELECT.PRICE.CUSTOMER', value: ConstantsFordProtect.PRICE_CUSTOMER },
    { label: 'TRANSLATE.SELECT.SELLER.PRICE', value: ConstantsFordProtect.SELLER_PRICE },
    { label: 'TRANSLATE.SELECT.COST.FORD', value: ConstantsFordProtect.COST_FORD },
    { label: 'TRANSLATE.SELECT.COST.WARRANTY', value: ConstantsFordProtect.COST_WARRANTY },
    { label: 'TRANSLATE.SELECT.COST.SERVICE', value: ConstantsFordProtect.COST_SERVICE }
  ];

  listCurrency = [
    { label: 'TRANSLATE.SELECT' },
    { label: 'TRANSLATE.SELECT.COUNTRY.CURRENCY.BRA', value: ConstantsFordProtect.REAL },
    { label: 'TRANSLATE.SELECT.COUNTRY.CURRENCY.OTHERS', value: ConstantsFordProtect.PESO }
  ];

  selectedNamePrice: string | undefined = '';
  selectedCurrency: string | undefined = '';
  previousCurrency: string | undefined = ''; // ← Store previous currency to detect changes
  inputPrice: number | undefined = 0;
  inputDescription: string | undefined = '';
  selectDisabled: boolean = false;
  id: number = 0;
  price: PriceEdit = {} as PriceEdit;
  editedPrice: PriceEdit = {} as PriceEdit;
  blockedUi = false;
  frontPrice: string = '';
  finalPrice: number = 0;
  isEditingMode = false;
  hasAttemptedSave = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private priceService: PriceService,
    private apsPermissionService: ApsPermissionService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private onlyAcceptNumbers: OnlyAcceptNumbers

  ) { }

  ngOnInit() {

    this.route.paramMap.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      this.id = +(params.get('id') ?? 0);
      // Load price data with proper initialization
      this.searchById();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.buttonCancel();
    }, 0);
  }

  buttonReturn() {
    this.router.navigate(['view-plans'], { skipLocationChange: false, replaceUrl: false });
  }

  namePriceChange(event: DropdownChangeEvent<unknown>) {
    this.selectedNamePrice = this.normalizeSelectValue(event.value);
  }

  currencyChange(event: DropdownChangeEvent<unknown>) {
    // Use the newly selected currency from event (not from this.price.itemPriceUnit)
    // because ngModel binding happens BEFORE onChange, so this.price.itemPriceUnit is already updated
    const newCurrency = this.normalizeSelectValue(event.value);

    // Get the actual previous currency with auto-detection fallback for race conditions
    let effectivePreviousCurrency = this.previousCurrency;
    if (!effectivePreviousCurrency || effectivePreviousCurrency.trim() === '') {
      // Fallback: detect from display value if previousCurrency is invalid (timing issue)
      effectivePreviousCurrency = this.frontPrice
        ? this.onlyAcceptNumbers.detectCurrencyFormat(this.frontPrice)
        : '$'; // Default to en-US
    }

    // If currency actually changed and we have a displayed price, reformat it
    if (effectivePreviousCurrency !== newCurrency && this.frontPrice) {
      this.reformatPriceForNewCurrency(effectivePreviousCurrency, newCurrency || '$');
    }

    // Update tracking variables
    this.selectedCurrency = newCurrency;
    this.previousCurrency = newCurrency; // Store for next change detection
  }

  /**
   * Re-formats the displayed price (frontPrice) when currency changes.
   * CRITICAL: Handles currency switches with proper decimal separator conversion.
   * 
   * previousCurrency is already validated before calling this function,
   * so it's guaranteed to be a valid currency format ('$' or 'R$').
   * 
   * Example flows:
   * - $ 3,254.00 (3 thousand, 254 dollars) → R$ → 3.254,00 ✓
   * - R$ 3.254,00 → $ → 3,254.00 ✓
   * - $ 0.50 → R$ → 0,50 ✓
   */
  private reformatPriceForNewCurrency(
    previousCurrency: string,
    newCurrency: string
  ): void {
    // Get the current display value — could be from frontPrice OR from the input DOM
    let displayValue = this.frontPrice;
    const inputElement = document.getElementById('valuePrice') as HTMLInputElement;
    if (inputElement && inputElement.value) {
      displayValue = inputElement.value;
    }

    if (!displayValue || displayValue.trim() === '') {
      return;
    }

    // Parse the displayed value with the PREVIOUS currency format to get the pure numeric value
    const numericValue = this.onlyAcceptNumbers.parseFormattedPriceToNumber(
      displayValue,
      previousCurrency
    );

    // If parsing failed or resulted in invalid number, skip
    if (!numericValue || numericValue <= 0 || isNaN(numericValue)) {
      return;
    }

    // Reformat the numeric value with the NEW currency format
    const reformattedPrice = this.onlyAcceptNumbers.formattingFrontPrice(
      numericValue,
      newCurrency
    );

    // Update all synchronization points:

    // 1. Update property binding
    this.frontPrice = reformattedPrice;
    this.price.itemPrice = numericValue;

    // 2. Update DOM input immediately (critical for visual feedback)
    if (inputElement) {
      inputElement.value = reformattedPrice;
    }

    // 3. Force Angular change detection to sync all ngModel bindings
    this.cdr.detectChanges();
  }

  private normalizeSelectValue(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'object') {
      // PrimeNG passes the entire option object when no optionValue is set and the item has no value property
      return this.normalizeSelectValue((value as Record<string, unknown>)['value']);
    }
    const normalized = String(value).trim();
    if (!normalized || normalized === 'TRANSLATE.SELECT') {
      return undefined;
    }
    return normalized;
  }

  private sanitizeDescription(value: string | null | undefined): string {
    return (value ?? '')
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  // Ensures that a price name from the backend exists in the dropdown list.
  // If the value is not found in listNamePrice, it is added dynamically.
  // This prevents the placeholder from showing when the backend returns an unexpected value.
  private ensurePriceNameInList(itemName: string | null | undefined): void {
    if (!itemName || itemName === 'TRANSLATE.SELECT') {
      return; // Skip null, undefined, or placeholder values
    }

    // Check if the value already exists in the list (by value field)
    const exists = this.listNamePrice.some((item) => item.value === itemName);

    if (!exists) {
      // Add the backend value as a dynamic option with label as the raw value
      // This allows the dropdown to display it while maintaining integrity of predefined options
      this.listNamePrice.push({
        label: itemName, // Display the raw value from backend
        value: itemName  // Maintain it as the matching value
      });
    }
  }

  // Ensures that a currency from the backend exists in the dropdown list.
  // If the value is not found in listCurrency, it is added dynamically.
  // This prevents the placeholder from showing when the backend returns an unexpected currency.
  private ensureCurrencyInList(currency: string | null | undefined): void {
    if (!currency || currency === 'TRANSLATE.SELECT') {
      return; // Skip null, undefined, or placeholder values
    }

    // Check if the value already exists in the list (by value field)
    const exists = this.listCurrency.some((item) => item.value === currency);

    if (!exists) {
      // Add the backend value as a dynamic option with label as the raw value
      // This allows the dropdown to display it while maintaining integrity of predefined options
      this.listCurrency.push({
        label: currency, // Display the raw value from backend
        value: currency  // Maintain it as the matching value
      });
    }
  }

  priceFormatting(price: Event) {
    // Pass the currency to format price correctly based on locale (R$ vs $)
    const valor = this.onlyAcceptNumbers.formatPriceForInputAndDatabase(price, this.price.itemPriceUnit);
    this.inputPrice = Number(valor);
  }

  private parseDisplayedPriceValue(rawPriceInput: string, currency: string | undefined): number | undefined {
    if (!rawPriceInput) {
      return undefined;
    }

    let normalizedValue = rawPriceInput;
    if (currency === 'R$') {
      normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
    } else {
      normalizedValue = normalizedValue.replace(/,/g, '');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
  }

  buttonEdit() {
    this.isEditingMode = true;
    this.hasAttemptedSave = false;
    this.clearValidationState();

    (document.getElementById("valuePrice") as HTMLInputElement).disabled = false;
    (document.getElementById("priceDescription") as HTMLInputElement).disabled = false;
    this.selectDisabled = false;

    (document.getElementById("titleDetails") as HTMLInputElement).style.display = 'none';
    (document.getElementById("titleEdit") as HTMLInputElement).style.display = 'block';
    (document.getElementById("subTitle") as HTMLInputElement).style.display = 'block';

  }

  buttonCancel() {
    this.isEditingMode = false;
    this.hasAttemptedSave = false;
    this.clearValidationState();

    (document.getElementById("valuePrice") as HTMLInputElement).disabled = true;
    (document.getElementById("priceDescription") as HTMLInputElement).disabled = true;
    this.selectDisabled = true;
    (document.getElementById("titleDetails") as HTMLInputElement).style.display = 'block';
    (document.getElementById("titleEdit") as HTMLInputElement).style.display = 'none';
    (document.getElementById("subTitle") as HTMLInputElement).style.display = 'none';

    this.restoringValues();
  }

  private clearValidationState() {
    this.inputPrice = undefined;
    this.inputDescription = undefined;
    // DO NOT RESET previousCurrency here - it will be re-initialized by searchById()
    // during restore/cancel flow. Zeroing it creates race conditions with dropdown clicks.
    if (this.selectedNamePrice === null) {
      this.selectedNamePrice = undefined;
    }
    if (this.selectedCurrency === null) {
      this.selectedCurrency = undefined;
    }
  }

  buttonSave() {

    this.hasAttemptedSave = true;
    this.blockedUi = false;
    let isValid = true;

    // Use price.itemName / price.itemPriceUnit as source of truth — they are the ngModel bindings
    // and always reflect the current dropdown selection, including when the placeholder is chosen
    this.selectedNamePrice = this.normalizeSelectValue(this.price.itemName);
    this.selectedCurrency = this.normalizeSelectValue(this.price.itemPriceUnit);

    const rawPriceInput = (document.getElementById("valuePrice") as HTMLInputElement).value?.trim() ?? '';

    this.inputPrice = this.parseDisplayedPriceValue(rawPriceInput, this.price.itemPriceUnit);

    const rawDescription = (document.getElementById("priceDescription") as HTMLInputElement).value;
    this.inputDescription = this.sanitizeDescription(rawDescription);

    if (!this.selectedNamePrice) {

      this.selectedNamePrice = undefined;
      isValid = false;
    }
    if (!this.selectedCurrency) {

      this.selectedCurrency = undefined;
      isValid = false;
    }
    if (this.inputPrice === undefined) {
      this.inputPrice = undefined;
      isValid = false;
    } else if (this.inputPrice <= 0) {
      isValid = false;
    }
    if (!this.inputDescription || this.inputDescription.length < 4) {
      if (!this.inputDescription) {
        this.inputDescription = undefined;
      }
      isValid = false;
    }

    if (!isValid) {
      this.focusFirstInvalidField();
      if (!this.hasInlinePriceValidationError()) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
        });
      }
      return;
    }

    this.blockedUi = true;

    this.editedPrice.itemPrice = this.inputPrice!;
    this.editedPrice.itemName = this.selectedNamePrice!;
    this.editedPrice.itemPriceUnit = this.selectedCurrency!;
    this.editedPrice.itemDescription = this.inputDescription!;
    this.editedPrice.createUser = this.apsPermissionService.getUserCsid();
    this.updatePrice();

  }

  restoringValues() {
    // Reset UI dropdowns (but not previousCurrency - let searchById handle it)
    this.selectedNamePrice = undefined;
    this.selectedCurrency = undefined;
    // Reload from backend (searchById will initialize previousCurrency correctly)
    this.searchById();
  }

  isPriceDescriptionEmpty(): boolean {
    return this.isEditingMode && this.hasAttemptedSave && !this.inputDescription;
  }

  isPriceDescriptionTooShort(): boolean {
    return this.isEditingMode && this.hasAttemptedSave && !!this.inputDescription && this.inputDescription.length > 0 && this.inputDescription.length < 4;
  }

  isPriceValueEmpty(): boolean {
    return this.isEditingMode && this.hasAttemptedSave && this.inputPrice === undefined;
  }

  isPriceValueZeroOrLess(): boolean {
    return this.isEditingMode && this.hasAttemptedSave && this.inputPrice !== undefined && this.inputPrice <= 0;
  }

  private hasPriceDescriptionMinLengthError(): boolean {
    return this.isPriceDescriptionTooShort();
  }

  private hasInlinePriceValidationError(): boolean {
    return this.isPriceValueEmpty() || this.isPriceValueZeroOrLess() || this.hasPriceDescriptionMinLengthError();
  }

  searchById() {

    this.blockedUi = true;
    this.priceService.getByPriceId(this.id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      this.price.priceId = response.id;
      this.price.itemName = response.itemName;
      this.price.itemDescription = response.itemDescription;
      this.price.itemPrice = response.itemPrice;

      this.frontPrice = this.onlyAcceptNumbers
        .formattingFrontPrice(response.itemPrice, response.itemPriceUnit);

      this.price.itemPriceUnit = response.itemPriceUnit;

      // Initialize previousCurrency tracking with the loaded currency
      // This ensures currency change detection works correctly on subsequent changes
      this.previousCurrency = response.itemPriceUnit;

      this.selectedCurrency = this.normalizeSelectValue(response.itemPriceUnit);
      this.selectedNamePrice = this.normalizeSelectValue(response.itemName);

      // If the backend price name or currency is not in the predefined lists, add them dynamically
      // This ensures the dropdowns display the values correctly instead of showing placeholder
      this.ensurePriceNameInList(response.itemName);
      this.ensureCurrencyInList(response.itemPriceUnit);

      this.blockedUi = false;
    }, (err) => {

      this.blockedUi = false;

      if (err === 400) {

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.PRICE.NOT.FOUND')
        });
      } else if (err.status === 401) {

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
        });

      } else {

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
        });
      }

    });

  }

  updatePrice() {

    this.priceService.updatePrice(this.editedPrice, this.price.priceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {

          this.blockedUi = false;
          this.buttonCancel();
          this.searchById();

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('TRANSLATE.SUCCESS'),
            detail: this.translateService.instant('TRANSLATE.SUCCESS.EDIT')
          });

        },
        error: (err) => {

          this.blockedUi = false;

          if (err.status === 400) {

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
            });

          } else if (err.status === 404) {

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.ERROR.SAVE.CHANGES')
            });

          } else if (err.status === 401) {

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
            });

          } else {

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
            });
          }
        }

      });
  }

  private focusFirstInvalidField(): void {
    if (!this.selectedNamePrice) {
      this.focusFieldById('namePrice');
      return;
    }

    if (!this.selectedCurrency) {
      this.focusFieldById('currency');
      return;
    }

    if (this.inputPrice === undefined || this.inputPrice <= 0) {
      this.focusFieldById('valuePrice');
      return;
    }

    if (this.isPriceDescriptionEmpty() || this.isPriceDescriptionTooShort()) {
      this.focusFieldById('priceDescription');
    }
  }

  private focusFieldById(elementId: string): void {
    setTimeout(() => {
      const host = document.getElementById(elementId) as HTMLElement | null;
      if (!host) {
        return;
      }

      const focusableChild = host.querySelector('input, textarea, .p-dropdown-label, .p-multiselect-label, [tabindex]') as HTMLElement | null;
      const target = focusableChild ?? host;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus();
    }, 0);
  }

}
